import type { SoftphoneDialpadProps } from './index.types';
import { SoftphoneDivider } from 'Modules/softphone/components/SoftphoneDivider';
import { isTerminalState } from 'Modules/softphone/constants';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession } from 'Modules/softphone/types';
import React, { useCallback } from 'react';
import { cn } from '~/lib/utils';
import { SoftphoneOnCallHeader } from '../SoftphoneOnCallHeader';
import { DialpadGrid } from './DialpadGrid';
import { DialpadInput } from './DialpadInput';

/**
 * Check if a call is in an active state (not terminal)
 */
const isCallActive = (call: CallSession): boolean => {
  return !isTerminalState(call.state);
};

/**
 * SoftphoneDialpad Component
 * Pre-call dialpad for entering phone numbers and initiating calls
 */
export const SoftphoneDialpad = ({
  className,
  testID,
  gridClassName,
}: SoftphoneDialpadProps): React.ReactElement => {
  const dialpadTestId = testID ? `${testID}-dialpad` : 'dialpad';

  const {
    activeCalls,
    setDialpadActive,
    handleDialpadDigitPress,
  } = useSoftphoneStore((state) => ({
    activeCalls: state.activeCalls,
    setDialpadActive: state.setDialpadActive,
    handleDialpadDigitPress: state.handleDialpadDigitPress as (digit: string) => void,
  }));

  const hasActiveCalls = Array.from(activeCalls.values()).some(isCallActive);

  const handleClose = useCallback(() => {
    setDialpadActive(false);
  }, [setDialpadActive]);

  return (
    <section
      className={cn(
        'w-full flex flex-col items-center px-[16px] gap-[8px]',
        hasActiveCalls ? 'pt-[4px]' : 'pt-[20px]',
        className
      )}
      data-testid={dialpadTestId}
      aria-label="Dialpad for call initiation"
    >
      {hasActiveCalls && (
        <SoftphoneOnCallHeader testID={dialpadTestId} onClose={handleClose} />
      )}

      <DialpadInput testID={dialpadTestId} />

      <SoftphoneDivider testID={dialpadTestId} />

      <DialpadGrid
        testID={dialpadTestId}
        className={gridClassName}
        onDigitPress={handleDialpadDigitPress}
      />
    </section>
  );
};
